import { api } from '@/api';
import { getCustomerProfile } from '@/features/auth/authService';
import { fetchProfileBookings } from '@/features/profile/profileService';
import { createAsyncThunk } from '@/store/utils';
import type {
  CustomerProfileDetailsResponseDTO,
  GetVouchersResponseDTO,
} from '@/types/dto/agent';
import type {
  ConsentDecisionModelDTO,
  ConsentDecisionsApplyModelDTO,
  ConsentDecisionsGetModelDTO,
  CustomerProfileGetModelDTO,
} from '@/types/dto/retailer';
import type {
  BookingSearchResponse,
  UpdateCustomerProfileRequest,
} from '@/types/profile';
import { clearTokens, getTokensRef } from '@/utils/auth';
import { createAction } from '@reduxjs/toolkit';
import axios from 'axios';

export const clearProfile = createAction('profile/clearProfile');
export const clearBookings = createAction('profile/clearBookings');

export const getProfile = createAsyncThunk<
  CustomerProfileDetailsResponseDTO | null,
  { clearSessionOnAuthError?: boolean } | void
>('profile/getProfile', async (arg) => {
  const tokensRef = getTokensRef();

  if (!tokensRef) {
    return null;
  }

  try {
    return await getCustomerProfile();
  } catch (error) {
    if (
      arg?.clearSessionOnAuthError &&
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status < 500
    ) {
      clearTokens();
    }

    console.error('Failed to fetch profile:', error);
    return null;
  }
});

export const updateProfile = createAsyncThunk<
  CustomerProfileGetModelDTO,
  UpdateCustomerProfileRequest
>('profile/updateProfile', async (profileData) => {
  // Self-service endpoint: the retailer API resolves the profile from the
  // auth/Requestor context, so no customerProfileId is sent. The by-id variant
  // (CustomersProfiles_UpdateCustomerProfile) is the admin one — see BR-57421.
  return (
    await api.retailerApi.CustomersProfile_UpdateCustomerProfile(
      null,
      profileData
    )
  ).data;
});

export const deleteProfile = createAsyncThunk<void, void>(
  'profile/deleteProfile',
  async () => {
    await api.agentApi.CustomerProfile_DeleteCustomerProfile();
  }
);

export const getProfileConsents = createAsyncThunk<ConsentDecisionsGetModelDTO>(
  'profile/getProfileConsents',
  async () => {
    return (await api.retailerApi.CustomersProfile_GetConsents()).data;
  }
);

export const saveProfileConsents = createAsyncThunk<
  void,
  ConsentDecisionModelDTO[]
>('profile/saveProfileConsents', async (consents, { dispatch }) => {
  // The retailer API resolves the profile from the auth/Requestor context, so
  // no customerProfileId is sent.
  const body: ConsentDecisionsApplyModelDTO = { consents };
  await api.retailerApi.CustomersProfile_AddConsents(null, body);

  // Refetch so the form's baseline reflects the persisted decisions and the
  // dirty state resets after a successful save.
  await dispatch(getProfileConsents());
});

export const getProfileActiveTickets = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileActiveTickets', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileId: `${current?.id}`,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileHistoryTickets = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileHistoryTickets', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileId: `${current?.id}`,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileActiveTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileActiveTravelPasses', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileId: `${current?.id}`,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});

export const getProfileHistoryTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileHistoryTravelPasses', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileId: `${current?.id}`,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});

export const getProfileActiveVouchers =
  createAsyncThunk<GetVouchersResponseDTO>(
    'profile/getProfileActiveVouchers',
    async () => {
      return (
        await api.agentApi.CustomerProfile_GetCustomerProfileVouchers({
          ActivityType: 'ACTIVE',
        })
      ).data;
    }
  );

export const getProfileHistoryVouchers =
  createAsyncThunk<GetVouchersResponseDTO>(
    'profile/getProfileHistoryVouchers',
    async () => {
      return (
        await api.agentApi.CustomerProfile_GetCustomerProfileVouchers({
          ActivityType: 'INACTIVE',
        })
      ).data;
    }
  );

export const deactivateProfileCards = createAsyncThunk<void, string[]>(
  'profile/deactivateProfileCards',
  async (ids, { dispatch }) => {
    await api.agentApi.CustomerProfileCards_DeactivateCustomerProfileCards(
      null,
      { ids }
    );
    await dispatch(getProfile());
  }
);

export const applyProfileCard = createAsyncThunk<void, { cardTypeId: string }>(
  'profile/applyProfileCard',
  async ({ cardTypeId }, { dispatch, getState }) => {
    // Removing a card deactivates its row rather than deleting it, so adding the
    // same type again has to update that row. A fresh id would leave the
    // deactivated card in place and the type would stay unavailable.
    const existingCard = getState().profile.current?.cards?.find(
      (card) => card.cardTypeId === cardTypeId
    );

    await api.agentApi.CustomerProfileCards_UpdateCustomerProfileCard(
      { cardId: existingCard?.id ?? crypto.randomUUID() },
      {
        cardTypeId,
        isActive: true,
        ...(existingCard?.uid ? { uid: existingCard.uid } : {}),
      }
    );
    await dispatch(getProfile());
  }
);
